// src/context/AppCacheContext.tsx
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { Client } from "@microsoft/microsoft-graph-client";
import { Application } from "@microsoft/microsoft-graph-types";
import { Button, Typography, Alert, AlertTitle, Box } from '@mui/material';

// Types for ownership tracking
interface OwnerInfo {
  appId: string;
  owners: Array<{ id: string; displayName?: string }>;
}

interface AppCacheContextType {
  applications: Application[];
  loading: boolean;
  error: React.ReactNode | null;
  graphAccessToken: string;
  refreshApplications: () => Promise<void>;
  lastFetched: Date | null;
  isStale: boolean;
  appOwners: Record<string, OwnerInfo>;
  currentUserId: string | null;
  loadingOwners: boolean;
  ownedAppsCount: number;
  spMap: Record<string, string>;
  currentTenantId: string | null;
  
  // NEW: Cache invalidation methods
  invalidateApplication: (appId: string) => void;
  refreshApplication: (appId: string) => Promise<Application | null>;
  updateApplicationInCache: (updatedApp: Application) => void;
  createUpdateWrapper: (appId: string) => (updateFn: () => Promise<void>) => Promise<void>;
}

const AppCacheContext = createContext<AppCacheContextType>({
  applications: [],
  loading: true,
  error: null,
  graphAccessToken: '',
  refreshApplications: async () => {},
  lastFetched: null,
  isStale: false,
  appOwners: {},
  currentUserId: null,
  loadingOwners: false,
  ownedAppsCount: 0,
  spMap: {},
  currentTenantId: null,
  
  // NEW: Default implementations
  invalidateApplication: () => {},
  refreshApplication: async () => null,
  updateApplicationInCache: () => {},
  createUpdateWrapper: () => async () => {}
});

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// FIXED: Tenant-specific cache keys
const getTenantSpecificCacheKey = (tenantId: string, keyType: string): string => {
  return `appconfig_${keyType}_${tenantId}`;
};

const CACHE_KEYS = {
  APPLICATIONS: 'applications_cache',
  APPLICATIONS_TIMESTAMP: 'applications_timestamp',
  OWNERS: 'owners_cache', 
  OWNERS_TIMESTAMP: 'owners_timestamp',
  SP: 'sp_cache',
  SP_TIMESTAMP: 'sp_timestamp'
};

export const AppCacheProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { instance, accounts } = useMsal();
  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<React.ReactNode | null>(null);
  const [graphAccessToken, setGraphAccessToken] = useState<string>("");
  const [lastFetched, setLastFetched] = useState<Date | null>(null);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  const [appOwners, setAppOwners] = useState<Record<string, OwnerInfo>>({});
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [loadingOwners, setLoadingOwners] = useState<boolean>(false);
  const [spMap, setSpMap] = useState<Record<string, string>>({});

  // CRITICAL: Track current tenant
  const currentTenantId = accounts.length > 0 ? accounts[0].tenantId : null;
  const [lastTenantId, setLastTenantId] = useState<string | null>(null);

  const isStale = lastFetched ? (Date.now() - lastFetched.getTime()) > CACHE_DURATION : true;

  const [wasAuthenticated, setWasAuthenticated] = useState<boolean>(false);
  const isCurrentlyAuthenticated = accounts.length > 0;

  // Calculate owned apps count
  const ownedAppsCount = Object.values(appOwners).filter(ownerInfo => 
    ownerInfo.owners.some(owner => owner.id === currentUserId)
  ).length;

  // NEW: Cache invalidation methods
  const invalidateApplication = useCallback((appId: string) => {
    if (!currentTenantId) return;
    
    console.log(`🗑️ Cache: Invalidating application ${appId} for tenant ${currentTenantId}`);
    
    // Remove from in-memory cache
    setApplications(prevApps => 
      prevApps.filter(app => app.appId !== appId && app.id !== appId)
    );
    
    // Remove from localStorage cache
    try {
      const appsKey = getTenantSpecificCacheKey(currentTenantId, CACHE_KEYS.APPLICATIONS);
      const cachedApps = localStorage.getItem(appsKey);
      
      if (cachedApps) {
        const parsedApps = JSON.parse(cachedApps);
        const filteredApps = parsedApps.filter((app: Application) => 
          app.appId !== appId && app.id !== appId
        );
        
        localStorage.setItem(appsKey, JSON.stringify(filteredApps));
        console.log(`💾 Cache: Updated localStorage cache for tenant ${currentTenantId}`);
      }
    } catch (error) {
      console.warn('Failed to update localStorage cache:', error);
    }
  }, [currentTenantId]);

  // NEW: Refresh single application from Graph
  const refreshApplication = useCallback(async (appId: string): Promise<Application | null> => {
    if (!graphAccessToken || !currentTenantId) return null;
    
    try {
      const client = Client.init({
        authProvider: (done) => done(null, graphAccessToken),
      });
      
      console.log(`🔄 Cache: Refreshing application ${appId} from Graph for tenant ${currentTenantId}`);
      
      // Find the application by appId or id
      const response = await client.api(`/applications`)
        .filter(`appId eq '${appId}' or id eq '${appId}'`)
        .select("id,appId,displayName,signInAudience,web,spa,tags,api,passwordCredentials,appRoles,requiredResourceAccess,optionalClaims,identifierUris,createdDateTime")
        .get();
      
      if (response.value && response.value.length > 0) {
        const freshApp = response.value[0];
        
        // Update in-memory cache
        setApplications(prevApps => 
          prevApps.map(app => 
            (app.appId === appId || app.id === appId) ? freshApp : app
          )
        );
        
        // Update localStorage cache
        try {
          const appsKey = getTenantSpecificCacheKey(currentTenantId, CACHE_KEYS.APPLICATIONS);
          const cachedApps = localStorage.getItem(appsKey);
          
          if (cachedApps) {
            const parsedApps = JSON.parse(cachedApps);
            const updatedApps = parsedApps.map((app: Application) => 
              (app.appId === appId || app.id === appId) ? freshApp : app
            );
            
            localStorage.setItem(appsKey, JSON.stringify(updatedApps));
            console.log(`💾 Cache: Updated application in localStorage for tenant ${currentTenantId}`);
          }
        } catch (error) {
          console.warn('Failed to update application in localStorage cache:', error);
        }
        
        console.log(`✅ Cache: Refreshed application ${appId} for tenant ${currentTenantId}`);
        return freshApp;
      }
      
      return null;
    } catch (error) {
      console.error(`❌ Cache: Failed to refresh application ${appId} for tenant ${currentTenantId}:`, error);
      return null;
    }
  }, [graphAccessToken, currentTenantId]);

  // NEW: Update application directly in cache (optimistic update)
  const updateApplicationInCache = useCallback((updatedApp: Application) => {
    if (!currentTenantId || !updatedApp.appId) return;
    
    console.log(`📝 Cache: Updating application ${updatedApp.appId} in cache for tenant ${currentTenantId}`);
    
    // Update in-memory cache
    setApplications(prevApps => 
      prevApps.map(app => 
        (app.appId === updatedApp.appId || app.id === updatedApp.id) 
          ? updatedApp 
          : app
      )
    );
    
    // Update localStorage cache
    try {
      const appsKey = getTenantSpecificCacheKey(currentTenantId, CACHE_KEYS.APPLICATIONS);
      const cachedApps = localStorage.getItem(appsKey);
      
      if (cachedApps) {
        const parsedApps = JSON.parse(cachedApps);
        const updatedApps = parsedApps.map((app: Application) => 
          (app.appId === updatedApp.appId || app.id === updatedApp.id) 
            ? updatedApp 
            : app
        );
        
        localStorage.setItem(appsKey, JSON.stringify(updatedApps));
        console.log(`💾 Cache: Updated application in localStorage for tenant ${currentTenantId}`);
      }
    } catch (error) {
      console.warn('Failed to update application in localStorage cache:', error);
    }
  }, [currentTenantId]);

  // NEW: Create a wrapper that automatically handles cache refresh
  const createUpdateWrapper = useCallback((appId: string) => {
    return async (updateFn: () => Promise<void>) => {
      try {
        // 1. Execute the update function (applies changes to Entra ID)
        await updateFn();
        
        // 2. Refresh the application in cache
        await refreshApplication(appId);
        
        console.log(`✅ Cache: Application ${appId} updated and cache refreshed for tenant ${currentTenantId}`);
        
      } catch (error) {
        console.error(`❌ Cache: Update failed for application ${appId} in tenant ${currentTenantId}:`, error);
        
        // If update fails, invalidate cache to prevent stale data
        invalidateApplication(appId);
        
        // Re-throw the error so components can handle it
        throw error;
      }
    };
  }, [refreshApplication, invalidateApplication, currentTenantId]);

  const clearAllCache = useCallback(() => {
    console.log('🗑️ Cache: Clearing all cached data due to logout');
    
    // Clear in-memory state
    setApplications([]);
    setAppOwners({});
    setSpMap({});
    setLastFetched(null);
    setLoading(true);
    setError(null);
    setGraphAccessToken("");
    setLastTenantId(null);
    
    // Clear ALL tenant caches from localStorage
    try {
      // Get all localStorage keys that start with 'appconfig_'
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('appconfig_')) {
          keysToRemove.push(key);
        }
      }
      
      // Remove all AppConfig cache keys
      keysToRemove.forEach(key => {
        localStorage.removeItem(key);
      });
      
      console.log(`🗑️ Cache: Removed ${keysToRemove.length} cache entries from localStorage`);
      
    } catch (error) {
      console.error('❌ Cache: Error clearing localStorage cache:', error);
      
      // Fallback: try to clear specific known patterns
      try {
        Object.values(CACHE_KEYS).forEach(keyType => {
          // Try to remove for common tenant patterns
          const patterns = ['common', 'organizations', currentTenantId, lastTenantId].filter(Boolean);
          patterns.forEach(pattern => {
            if (pattern) {
              const key = getTenantSpecificCacheKey(pattern as string, keyType);
              localStorage.removeItem(key);
            }
          });
        });
        console.log('🗑️ Cache: Fallback cache cleanup completed');
      } catch (fallbackError) {
        console.error('❌ Cache: Fallback cache cleanup failed:', fallbackError);
      }
    }
  }, [currentTenantId, lastTenantId]);  

  // NEW: Detect logout and clear cache
  useEffect(() => {
    // User was authenticated but now isn't = logout occurred
    if (wasAuthenticated && !isCurrentlyAuthenticated) {
      console.log('👋 Auth: User logout detected, clearing all cache');
      clearAllCache();
    }
    
    // Update authentication tracking
    setWasAuthenticated(isCurrentlyAuthenticated);
  }, [isCurrentlyAuthenticated, wasAuthenticated, clearAllCache]);  

  // CRITICAL: Clear cache when tenant changes
  useEffect(() => {
    if (currentTenantId && lastTenantId && currentTenantId !== lastTenantId) {
      console.log(`🔄 Tenant changed from ${lastTenantId} to ${currentTenantId} - clearing cache`);
      
      // Clear in-memory state immediately
      setApplications([]);
      setAppOwners({});
      setSpMap({});
      setLastFetched(null);
      setLoading(true);
      setError(null);
      setGraphAccessToken("");
      
      // Clear localStorage for old tenant (cleanup)
      try {
        Object.values(CACHE_KEYS).forEach(keyType => {
          const oldKey = getTenantSpecificCacheKey(lastTenantId, keyType);
          localStorage.removeItem(oldKey);
        });
        console.log(`🗑️ Cleared cache for previous tenant: ${lastTenantId}`);
      } catch (cleanupError) {
        console.warn('Error cleaning up old tenant cache:', cleanupError);
      }
      
      // Reset load state to trigger fresh load for new tenant
      setInitialLoadComplete(false);
    }
    
    if (currentTenantId) {
      setLastTenantId(currentTenantId);
    }
  }, [currentTenantId, lastTenantId]);

  // Get current user ID when available
  useEffect(() => {
    if (accounts.length > 0 && accounts[0].localAccountId) {
      setCurrentUserId(accounts[0].localAccountId);
    } else {
      setCurrentUserId(null);
    }
  }, [accounts]);

  // FIXED: Tenant-specific cache loading
  useEffect(() => {
    if (!currentTenantId) return;
    
    const loadOwnersFromCache = () => {
      try {
        const ownersKey = getTenantSpecificCacheKey(currentTenantId, CACHE_KEYS.OWNERS);
        const timestampKey = getTenantSpecificCacheKey(currentTenantId, CACHE_KEYS.OWNERS_TIMESTAMP);
        
        const cachedOwners = localStorage.getItem(ownersKey);
        const cachedOwnersTimestamp = localStorage.getItem(timestampKey);
        
        if (cachedOwners && cachedOwnersTimestamp) {
          const timestamp = new Date(cachedOwnersTimestamp);
          const age = Date.now() - timestamp.getTime();
          
          if (age < CACHE_DURATION) {
            console.log(`👥 Owners: Loading from cache for tenant ${currentTenantId} (${Math.round(age / 1000)}s old)`);
            setAppOwners(JSON.parse(cachedOwners));
          } else {
            localStorage.removeItem(ownersKey);
            localStorage.removeItem(timestampKey);
          }
        }
      } catch (error) {
        console.warn('👥 Owners: Failed to load from cache:', error);
      }
    };

    const loadSpFromCache = () => {
      try {
        const spKey = getTenantSpecificCacheKey(currentTenantId, CACHE_KEYS.SP);
        const timestampKey = getTenantSpecificCacheKey(currentTenantId, CACHE_KEYS.SP_TIMESTAMP);
        
        const cachedSp = localStorage.getItem(spKey);
        const cachedSpTimestamp = localStorage.getItem(timestampKey);
        
        if (cachedSp && cachedSpTimestamp) {
          const timestamp = new Date(cachedSpTimestamp);
          const age = Date.now() - timestamp.getTime();
          
          if (age < CACHE_DURATION) {
            console.log(`🛡️ Service Principals: Loading from cache for tenant ${currentTenantId} (${Math.round(age / 1000)}s old)`);
            setSpMap(JSON.parse(cachedSp));
          } else {
            localStorage.removeItem(spKey);
            localStorage.removeItem(timestampKey);
          }
        }
      } catch (error) {
        console.warn('🛡️ Service Principals: Failed to load from cache:', error);
      }
    };

    loadOwnersFromCache();
    loadSpFromCache();
  }, [currentTenantId]);

  // FIXED: Tenant-specific application cache loading
  useEffect(() => {
    if (!currentTenantId) return;
    
    const loadFromCache = () => {
      try {
        const appsKey = getTenantSpecificCacheKey(currentTenantId, CACHE_KEYS.APPLICATIONS);
        const timestampKey = getTenantSpecificCacheKey(currentTenantId, CACHE_KEYS.APPLICATIONS_TIMESTAMP);
        
        const cachedApps = localStorage.getItem(appsKey);
        const cachedTimestamp = localStorage.getItem(timestampKey);
        
        if (cachedApps && cachedTimestamp) {
          const timestamp = new Date(cachedTimestamp);
          const age = Date.now() - timestamp.getTime();
          
          if (age < CACHE_DURATION) {
            const parsedApps = JSON.parse(cachedApps);
            console.log(`📱 Apps: Loading ${parsedApps.length} applications from cache for tenant ${currentTenantId} (${Math.round(age / 1000)}s old)`);
            setApplications(parsedApps);
            setLastFetched(timestamp);
            setLoading(false);
            setError(null);
            return true;
          } else {
            console.log(`📱 Apps: Cache expired for tenant ${currentTenantId} (${Math.round(age / 1000)}s old), will refresh`);
            localStorage.removeItem(appsKey);
            localStorage.removeItem(timestampKey);
          }
        }
      } catch (error) {
        console.warn(`📱 Apps: Failed to load from cache for tenant ${currentTenantId}:`, error);
        // Clean up corrupted cache
        const appsKey = getTenantSpecificCacheKey(currentTenantId, CACHE_KEYS.APPLICATIONS);
        const timestampKey = getTenantSpecificCacheKey(currentTenantId, CACHE_KEYS.APPLICATIONS_TIMESTAMP);
        localStorage.removeItem(appsKey);
        localStorage.removeItem(timestampKey);
      }
      return false;
    };

    const hasValidCache = loadFromCache();
    setInitialLoadComplete(!hasValidCache);
  }, [currentTenantId]);

  const loadApplicationsWithCache = useCallback(async (forceRefresh = false) => {
    if (!instance || accounts.length === 0 || !currentTenantId) {
      console.log('📱 Apps: Skipping load - MSAL not ready or no tenant');
      return;
    }

    if (!forceRefresh && !isStale && applications.length > 0) {
      console.log(`📱 Apps: Using cached applications for tenant ${currentTenantId}`);
      return;
    }

    if (!forceRefresh && !initialLoadComplete) {
      console.log('📱 Apps: Skipping load - cache check incomplete');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const appOnlyRequest = {
        scopes: ["Application.ReadWrite.All"],
        account: accounts[0],
      };

      const resp = await instance.acquireTokenSilent(appOnlyRequest);
      setGraphAccessToken(resp.accessToken);
      
      const client = Client.init({
        authProvider: (done) => done(null, resp.accessToken),
      });
      
      console.log(`📱 Apps: ${forceRefresh ? 'Force refreshing' : 'Loading'} applications from Microsoft Graph for tenant ${currentTenantId}...`);
      
      const allApplications: Application[] = [];
      let nextLink: string | undefined;
      let pageCount = 0;
      const MAX_PAGES = 100;
      let consecutiveEmptyPages = 0;
      const MAX_CONSECUTIVE_EMPTY = 3;
      
      do {
        pageCount++;
        console.log(`📱 Apps: Fetching page ${pageCount} for tenant ${currentTenantId}...`);
        
        let request = client.api("/applications")
          .select("id,appId,displayName,signInAudience,web,spa,tags,api,passwordCredentials,appRoles,requiredResourceAccess,optionalClaims,identifierUris,createdDateTime")
          .top(100);
        
        if (nextLink) {
          try {
            const url = new URL(nextLink);
            const skipToken = url.searchParams.get('$skiptoken');
            if (skipToken) {
              // FIXED: Always re-apply the complete .select() for pagination
              request = client.api("/applications")
                .select("id,appId,displayName,signInAudience,web,spa,tags,api,passwordCredentials,appRoles,requiredResourceAccess,optionalClaims,identifierUris,createdDateTime")
                .top(100)
                .skipToken(skipToken);
            } else {
              // FIXED: Always re-apply .select() even for direct URL usage
              const fullUrl = nextLink.replace('https://graph.microsoft.com/v1.0', '');
              request = client.api(fullUrl)
                .select("id,appId,displayName,signInAudience,web,spa,tags,api,passwordCredentials,appRoles,requiredResourceAccess,optionalClaims,identifierUris,createdDateTime");
            }
          } catch (urlError) {
            console.error(`📱 Apps: Error parsing nextLink: ${nextLink}`, urlError);
            break;
          }
        }
        
        const response = await request.get();
        
        if (response.value && response.value.length > 0) {
          allApplications.push(...response.value);
          console.log(`📱 Apps: Page ${pageCount}: Added ${response.value.length} applications (Total: ${allApplications.length}) for tenant ${currentTenantId}`);
          consecutiveEmptyPages = 0;
        } else {
          consecutiveEmptyPages++;
          if (consecutiveEmptyPages >= MAX_CONSECUTIVE_EMPTY) {
            console.warn(`📱 Apps: Too many consecutive empty pages, stopping pagination`);
            break;
          }
        }
        
        nextLink = response['@odata.nextLink'];
        
        if (pageCount >= MAX_PAGES) {
          console.warn(`📱 Apps: Reached maximum page limit (${MAX_PAGES}). Stopping pagination.`);
          break;
        }
        
      } while (nextLink);
      
      console.log(`✅ Apps: Loaded ${allApplications.length} applications for tenant ${currentTenantId}, caching for ${CACHE_DURATION / 60000} minutes`);
      
      // FIXED: Cache applications immediately
      const now = new Date();
      try {
        const appsKey = getTenantSpecificCacheKey(currentTenantId, CACHE_KEYS.APPLICATIONS);
        const timestampKey = getTenantSpecificCacheKey(currentTenantId, CACHE_KEYS.APPLICATIONS_TIMESTAMP);
        
        localStorage.setItem(appsKey, JSON.stringify(allApplications));
        localStorage.setItem(timestampKey, now.toISOString());
        console.log(`💾 Apps: Cached ${allApplications.length} applications for tenant ${currentTenantId}`);
      } catch (cacheError) {
        console.warn(`📱 Apps: Failed to cache applications for tenant ${currentTenantId}:`, cacheError);
      }
      
      // CRITICAL: Set applications and stop loading immediately
      setApplications(allApplications);
      setLastFetched(now);
      setError(null);
      setLoading(false); // Stop loading here to show apps immediately
      
      // IMPROVED: Load additional data in background without blocking UI
      console.log(`🔄 Apps: Starting background data loading for ${allApplications.length} applications...`);
      
      // Start background loading without awaiting
      Promise.allSettled([
        loadApplicationOwners(client, allApplications),
        loadServicePrincipals(client, allApplications)
      ]).then(() => {
        console.log(`✅ Apps: Background data loading completed for tenant ${currentTenantId}`);
      }).catch((backgroundError) => {
        console.warn(`⚠️ Apps: Background data loading failed for tenant ${currentTenantId}:`, backgroundError);
      });
      
    } catch (err: any) {
      console.error(`📱 Apps: Error loading applications for tenant ${currentTenantId}:`, err);
      
      if (err instanceof InteractionRequiredAuthError || 
          (err.message && (
            err.message.includes("consent_required") || 
            err.message.includes("interaction_required") ||
            err.message.includes("invalid_grant")
          ))) {
        setError(
          <Alert severity="error" sx={{ mt: 2 }}>
            <AlertTitle>Permission Required</AlertTitle>
            <Typography variant="body2" paragraph>
              This application requires additional permissions to access Microsoft Graph.
            </Typography>
            <Button 
              variant="contained" 
              onClick={() => {
                instance.acquireTokenRedirect({
                  scopes: ["Application.ReadWrite.All"],
                  account: accounts[0],
                  prompt: "consent"
                });
              }}
            >
              Grant Permissions
            </Button>
          </Alert>
        );
      } else {
        setError("Failed to load applications. Please try again later.");
      }
      
      setLoading(false);
    } finally {
      setInitialLoadComplete(true);
    }
  }, [instance, accounts, isStale, applications.length, initialLoadComplete, currentTenantId]);

  // FIXED: Load application owners with tenant-specific caching
  const loadApplicationOwners = async (client: Client, apps: Application[]) => {
    if (!currentTenantId) return;
    
    setLoadingOwners(true);
    console.log(`👥 Owners: Loading application owners for tenant ${currentTenantId}...`);
    
    try {
      const ownersData: Record<string, OwnerInfo> = {};
      
      // Check tenant-specific cached owners
      const ownersKey = getTenantSpecificCacheKey(currentTenantId, CACHE_KEYS.OWNERS);
      const timestampKey = getTenantSpecificCacheKey(currentTenantId, CACHE_KEYS.OWNERS_TIMESTAMP);
      
      const cachedOwners = localStorage.getItem(ownersKey);
      const cachedOwnersTimestamp = localStorage.getItem(timestampKey);
      
      if (cachedOwners && cachedOwnersTimestamp) {
        const timestamp = new Date(cachedOwnersTimestamp);
        const age = Date.now() - timestamp.getTime();
        
        if (age < CACHE_DURATION) {
          console.log(`👥 Owners: Using cached owners data for tenant ${currentTenantId} (${Math.round(age / 1000)}s old)`);
          setAppOwners(JSON.parse(cachedOwners));
          setLoadingOwners(false);
          return;
        }
      }
      
      // Batch processing to avoid rate limits (existing logic)
      const batchSize = 50;
      const batches = [];
      
      for (let i = 0; i < apps.length; i += batchSize) {
        batches.push(apps.slice(i, i + batchSize));
      }
      
      console.log(`👥 Owners: Processing ${apps.length} applications in ${batches.length} batches for tenant ${currentTenantId}`);
      
      for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
        const batch = batches[batchIndex];
        console.log(`👥 Owners: Processing batch ${batchIndex + 1}/${batches.length} (${batch.length} apps) for tenant ${currentTenantId}`);
        
        const promises = batch.map(async (app) => {
          if (!app.id) return null;
          
          try {
            const owners = await client.api(`/applications/${app.id}/owners`).get();
            return {
              appId: app.appId || app.id,
              owners: owners.value.map((owner: any) => ({
                id: owner.id,
                displayName: owner.displayName
              }))
            };
          } catch (ownerError: any) {
            if (!ownerError.message?.includes('does not exist') && 
                !ownerError.message?.includes('Request_ResourceNotFound')) {
              console.warn(`👥 Owners: Failed to load owners for ${app.displayName}:`, ownerError.message);
            }
            return null;
          }
        });
        
        const results = await Promise.allSettled(promises);
        
        results.forEach((result) => {
          if (result.status === 'fulfilled' && result.value) {
            ownersData[result.value.appId] = result.value;
          }
        });
        
        if (batchIndex < batches.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }
      
      // FIXED: Cache with tenant-specific keys
      const now = new Date();
      try {
        localStorage.setItem(ownersKey, JSON.stringify(ownersData));
        localStorage.setItem(timestampKey, now.toISOString());
        console.log(`💾 Owners: Cached owners data for tenant ${currentTenantId}`);
      } catch (cacheError) {
        console.warn(`👥 Owners: Failed to cache owners data for tenant ${currentTenantId}:`, cacheError);
      }
      
      setAppOwners(ownersData);
      console.log(`✅ Owners: Loaded owners for ${Object.keys(ownersData).length}/${apps.length} applications in tenant ${currentTenantId}`);
      
    } catch (error) {
      console.error(`👥 Owners: Error loading application owners for tenant ${currentTenantId}:`, error);
    } finally {
      setLoadingOwners(false);
    }
  };

  // FIXED: Load service principals with tenant-specific caching
  const loadServicePrincipals = async (client: Client, apps: Application[]) => {
    if (!currentTenantId) return;
    
    console.log(`🛡️ Service Principals: Loading service principals for tenant ${currentTenantId}...`);
    
    try {
      // Check tenant-specific cache
      const spKey = getTenantSpecificCacheKey(currentTenantId, CACHE_KEYS.SP);
      const timestampKey = getTenantSpecificCacheKey(currentTenantId, CACHE_KEYS.SP_TIMESTAMP);
      
      const cachedSp = localStorage.getItem(spKey);
      const cachedSpTimestamp = localStorage.getItem(timestampKey);
      
      if (cachedSp && cachedSpTimestamp) {
        const timestamp = new Date(cachedSpTimestamp);
        const age = Date.now() - timestamp.getTime();
        
        if (age < CACHE_DURATION) {
          console.log(`🛡️ Service Principals: Using cached data for tenant ${currentTenantId} (${Math.round(age / 1000)}s old)`);
          setSpMap(JSON.parse(cachedSp));
          return;
        }
      }
      
      const spData: Record<string, string> = {};
      
      const servicePrincipals = await client.api('/servicePrincipals')
        .select('id,appId,preferredSingleSignOnMode')
        .get();
      
      servicePrincipals.value.forEach((sp: any) => {
        if (sp.appId && sp.preferredSingleSignOnMode === 'saml') {
          spData[sp.appId] = 'saml';
        }
      });
      
      // FIXED: Cache with tenant-specific keys
      const now = new Date();
      try {
        localStorage.setItem(spKey, JSON.stringify(spData));
        localStorage.setItem(timestampKey, now.toISOString());
        console.log(`💾 Service Principals: Cached data for tenant ${currentTenantId}`);
      } catch (cacheError) {
        console.warn(`🛡️ Service Principals: Failed to cache data for tenant ${currentTenantId}:`, cacheError);
      }
      
      setSpMap(spData);
      console.log(`✅ Service Principals: Loaded ${Object.keys(spData).length} SAML service principals for tenant ${currentTenantId}`);
      
    } catch (error) {
      console.error(`🛡️ Service Principals: Error loading service principals for tenant ${currentTenantId}:`, error);
    }
  };

  const refreshApplications = useCallback(async () => {
    await loadApplicationsWithCache(true);
  }, [loadApplicationsWithCache]);

  // Auto-load applications when MSAL is ready
  useEffect(() => {
    if (instance && accounts.length > 0 && initialLoadComplete && currentTenantId) {
      loadApplicationsWithCache();
    }
  }, [instance, accounts, loadApplicationsWithCache, initialLoadComplete, currentTenantId]);

  // Auto-load owners when currentUserId becomes available
  useEffect(() => {
    const shouldLoadOwners = currentUserId && 
                            applications.length > 0 && 
                            Object.keys(appOwners).length === 0 && 
                            !loadingOwners &&
                            graphAccessToken &&
                            currentTenantId;

    if (shouldLoadOwners) {
      console.log(`👥 Owners: Current user ID available, loading owners for existing applications in tenant ${currentTenantId}`);
      
      const client = Client.init({
        authProvider: (done) => done(null, graphAccessToken),
      });
      
      loadApplicationOwners(client, applications);
    }
  }, [currentUserId, applications, appOwners, loadingOwners, graphAccessToken, currentTenantId]);

  return (
    <AppCacheContext.Provider value={{
      applications,
      loading,
      error,
      graphAccessToken,
      refreshApplications,
      lastFetched,
      isStale,
      appOwners,
      currentUserId,
      loadingOwners,
      ownedAppsCount,
      spMap,
      currentTenantId,
      
      // NEW: Cache invalidation methods
      invalidateApplication,
      refreshApplication,
      updateApplicationInCache,
      createUpdateWrapper
    }}>
      {children}
    </AppCacheContext.Provider>
  );
};

export const useAppCache = () => useContext(AppCacheContext);